import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ConfirmationModal,
  DateField,
  FlexColumn,
  FormModal,
  InputField,
  SelectField
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { required } from '@ubeya/shared/utils/form';
import moment from 'moment';

const Container = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 28px;
`;

const WarningBanner = styled.div`
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.yellowLight};
  border: 1px solid ${({ theme }) => theme.colors.yellowPartially};
  border-radius: 4px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.black};
`;

const WarningTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const ConfirmBody = styled(FlexColumn)`
  gap: 8px;
  font-size: 13px;
  line-height: 1.5;
`;

const ConfirmDetailRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  gap: 6px 12px;
`;

const ConfirmLabel = styled.span`
  color: ${({ theme }) => theme.colors.grey4};
  font-size: 12px;
`;

const ConfirmValue = styled.span`
  color: ${({ theme }) => theme.colors.black};
  font-size: 13px;
`;

const ExportLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  text-align: left;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const formatDate = (value) => (value ? moment(value).format(API_DATE_FORMAT) : undefined);

const YearResetModal = ({ onClose, yearReset, management }) => {
  const { t } = useContext(ConfigContext);

  // Manual year reset is non-reversible — pendingValues drives a confirm step before commit.
  const [pendingValues, setPendingValues] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // BE rejects non-Manual cycles; filter upstream.
  const leaveOptions = useMemo(
    () =>
      (management?.balances || [])
        .filter((b) => b.yearCycleKind === 'manual')
        .map((b) => ({ label: b.leaveTypeName, value: b.accountContractV2LeaveId })),
    [management]
  );

  const initialValues = useMemo(() => ({ resetDate: moment().format(API_DATE_FORMAT) }), []);

  // Stash values; FormModal would auto-close otherwise — confirm step lives below.
  const onSubmitForm = useCallback((values) => setPendingValues(values), []);

  const onCancelConfirm = useCallback(() => {
    if (isSubmitting) return;
    setPendingValues(null);
  }, [isSubmitting]);

  const onConfirmReset = useCallback(async () => {
    if (!pendingValues) return;
    setIsSubmitting(true);
    try {
      await yearReset({
        assignmentId: management.employeeContractV2Id,
        accountContractV2LeaveId: pendingValues.accountContractV2LeaveId,
        resetDate: formatDate(pendingValues.resetDate),
        reason: pendingValues.reason || undefined
      });
      onClose();
    } catch {
      // useOptimisticMutation already shows the toaster.
      setIsSubmitting(false);
    }
  }, [pendingValues, yearReset, management, onClose]);

  const selectedLeaveName = useMemo(() => {
    if (!pendingValues) return '';
    const opt = leaveOptions.find((option) => option.value === pendingValues.accountContractV2LeaveId);
    return opt?.label || '';
  }, [pendingValues, leaveOptions]);

  const selectedBalance = useMemo(() => {
    if (!pendingValues) return null;
    return (management?.balances || []).find(
      (b) => b.accountContractV2LeaveId === pendingValues.accountContractV2LeaveId
    );
  }, [pendingValues, management]);

  // Snapshot of every leaf's balance before the irreversible reset.
  const onExportCsv = useCallback(() => {
    const rows = (management?.balances || []).map((b) => ({
      employeeName: management?.employeeName ?? '',
      contractName: management?.contractName ?? '',
      leaveName: b.leaveTypeName ?? '',
      balance: b.balance ?? 0,
      unit: b.unit ?? ''
    }));
    if (!rows.length) return;
    const header = ['Employee', 'Contract', 'Leave', 'Balance', 'Unit'];
    const lines = [
      header.join(','),
      ...rows.map((r) =>
        [r.employeeName, r.contractName, r.leaveName, r.balance, r.unit]
          // CSV-quote each field; double-up any existing quote.
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(',')
      )
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leave-balances-${management?.employeeName || 'employee'}-${moment().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [management]);

  return (
    <>
      <FormModal
        onSubmit={onSubmitForm}
        initialValues={initialValues}
        size="small"
        requiredWarningFooter={t('missingRequiredFields')}
        onClose={onClose}
        title={t('manualYearReset')}
        confirmText={t('next')}
        showCancelButton
        keepDirtyOnReinitialize
        disabledOnPristine={false}>
        <Container>
          <WarningBanner>
            <WarningTitle>{t('yearReset_warning_title')}</WarningTitle>
            <div>{t('yearReset_warning_body')}</div>
          </WarningBanner>
          <SelectField
            name="accountContractV2LeaveId"
            title={t('leave')}
            options={leaveOptions}
            validate={required}
            required
            usePortal
            isMulti={false}
            clearable
          />
          <DateField name="resetDate" title={t('resetDate')} usePortal />
          <InputField name="reason" title={t('reason')} />
          {/* PM Q19: export every leaf's current balance before the reset so
              admin has a recoverable snapshot of pre-reset state. */}
          <ExportLink type="button" onClick={onExportCsv}>
            {t('yearReset_exportBalances')}
          </ExportLink>
        </Container>
      </FormModal>

      {pendingValues && (
        <ConfirmationModal
          usePortal
          isPermanentAction
          title={t('yearReset_confirm_title')}
          confirmText={t('yearReset_confirm_cta')}
          disabledConfirm={isSubmitting}
          loadingConfirm={isSubmitting}
          onConfirm={onConfirmReset}
          onClose={onCancelConfirm}>
          <ConfirmBody>
            <div>{t('yearReset_confirm_body')}</div>
            <ConfirmDetailRow>
              <ConfirmLabel>{t('leave')}</ConfirmLabel>
              <ConfirmValue>{selectedLeaveName || '-'}</ConfirmValue>
              <ConfirmLabel>{t('resetDate')}</ConfirmLabel>
              <ConfirmValue>{formatDate(pendingValues.resetDate) || '-'}</ConfirmValue>
              <ConfirmLabel>{t('reason')}</ConfirmLabel>
              <ConfirmValue>{pendingValues.reason || t('notSet')}</ConfirmValue>
              {selectedBalance && (
                <>
                  <ConfirmLabel>{t('yearReset_currentBalance')}</ConfirmLabel>
                  <ConfirmValue>
                    {selectedBalance.balance} {selectedBalance.unit}
                  </ConfirmValue>
                </>
              )}
            </ConfirmDetailRow>
            <ExportLink type="button" onClick={onExportCsv}>
              {t('yearReset_exportBalances')}
            </ExportLink>
          </ConfirmBody>
        </ConfirmationModal>
      )}
    </>
  );
};

export default YearResetModal;
